import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  useTheme,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { BsPersonFillCheck } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { UserRepository } from "../repositories/UserRepository";
import { useDispatch } from "react-redux";
import { triggerRefresh } from "../../store/GlobalSlice";
import { IoCheckmarkSharp, IoDocumentAttach } from "react-icons/io5";
import { IoIosSettings } from "react-icons/io";
import { UserValidator } from "../validators/UserValidator";
import {
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
} from "../../store/AlertSlice";
import { ErrorMessageResolver } from "../common/ErrorMessageResolver";

const sideItems = [
  {
    label: "Општи податоци",
    icon: <BsPersonFillCheck />,
  },
  {
    label: "Додади улоги",
    icon: <IoIosSettings />,
  },
];

export default function NewUser() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    selectedRoles: [],
  });
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userDataErrors, setUserDataErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getAllRoles();
  }, []);

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const create = async () => {
    setLoading(true);
    const payload = {
      ...formData,
      selectedRoles: selectedRoles,
    };

    const isValid = await validateForm(payload);
    if (!isValid) {
      setLoading(false);
      console.error("Form validation failed:", userDataErrors);
      return;
    }
    UserRepository.create(payload)
      .then((res) => {
        setLoading(false);
        dispatch(notifyShowSuccessMessage("Корисникот е успешно креиран."));
        dispatch(triggerRefresh("users"));
        navigate("/users");
      })
      .catch((err) => {
        setLoading(false);
        console.error("Error creating user:", err);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
      });
  };

  const validateForm = async (data) => {
    try {
      await UserValidator.validate(data, { abortEarly: false });
      setUserDataErrors({});
      return true;
    } catch (err) {
      const validationErrors = {};
      err.inner.forEach((validationError) => {
        validationErrors[validationError.path] = validationError.message;
      });
      setUserDataErrors(validationErrors);
      return false;
    }
  };

  const getAllRoles = () => {
    UserRepository.getAllRoles()
      .then((res) => {
        setRoles(res.data);
      })
      .catch((err) => {
        console.error("Error fetching roles:", err);
      });
  };

  const handleToggle = (role) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  return (
    <Dialog
      open
      onClose={() => navigate("/users")}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          display: "flex",
          flexDirection: "column",
        },
      }}
      style={{ zIndex: 1300 }}
    >
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}
        open={loading}
      >
        <CircularProgress color="inherit" size={60} thickness={4} />
      </Backdrop>
      <DialogTitle>Нов корисник</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: "10px" }}>
          <Grid
            item
            xs={12}
            style={{
              backgroundColor: "#fff",
              border: "1px solid #ededed",
              borderRadius: "4px",
              padding: "16px",
              marginLeft: "15px",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              style={{ marginBottom: "16px" }}
            >
              Општи податоци
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={12} style={{}}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Корисничко име"
                  autoComplete="off"
                  value={formData?.username || ""}
                  onChange={(event) => {
                    let value = event.target.value;
                    handleChange("username")({
                      ...event,
                      target: { ...event.target, value },
                    });
                  }}
                  error={userDataErrors.username ? true : false}
                  helperText={userDataErrors.username || ""}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Име"
                  autoComplete="off"
                  value={formData?.firstName || ""}
                  onChange={(event) => {
                    let value = event.target.value;
                    handleChange("firstName")({
                      ...event,
                      target: { ...event.target, value },
                    });
                  }}
                  error={userDataErrors.firstName ? true : false}
                  helperText={userDataErrors.firstName || ""}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Презиме"
                  autoComplete="off"
                  value={formData?.lastName || ""}
                  onChange={(event) => {
                    let value = event.target.value;
                    handleChange("lastName")({
                      ...event,
                      target: { ...event.target, value },
                    });
                  }}
                  error={userDataErrors.lastName ? true : false}
                  helperText={userDataErrors.lastName || ""}
                />
              </Grid>
              <Grid item xs={6} style={{}}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="E-пошта"
                  autoComplete="off"
                  value={formData?.email || ""}
                  onChange={(event) => {
                    let value = event.target.value;
                    handleChange("email")({
                      ...event,
                      target: { ...event.target, value },
                    });
                  }}
                  error={userDataErrors.email ? true : false}
                  helperText={userDataErrors.email || ""}
                />
              </Grid>
              <Grid item xs={6} style={{}}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Телефонски број"
                  autoComplete="off"
                  value={formData?.phoneNumber || ""}
                  onChange={(event) => {
                    let value = event.target.value;
                    handleChange("phoneNumber")({
                      ...event,
                      target: { ...event.target, value },
                    });
                  }}
                  error={userDataErrors.phoneNumber ? true : false}
                  helperText={userDataErrors.phoneNumber || ""}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{ marginBottom: "16px", marginTop: "10px" }}
                >
                  Улоги
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    flexDirection: "column",
                    marginTop: "10px",
                  }}
                >
                  {userDataErrors.selectedRoles && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ marginTop: 1 }}
                    >
                      {userDataErrors.selectedRoles}
                    </Typography>
                  )}

                  <div
                    style={{ gap: "8px", display: "flex", flexWrap: "wrap" }}
                  >
                    {roles.map((role) => {
                      const selected = selectedRoles.includes(role);
                      return (
                        <Chip
                          key={role}
                          label={
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: 12,
                                color: selected ? "#fff" : "#454545",
                              }}
                            >
                              {role}
                            </Typography>
                          }
                          icon={
                            selected ? (
                              <IoCheckmarkSharp
                                style={{ color: "#fff", fontSize: "16px" }}
                              />
                            ) : undefined
                          }
                          clickable
                          color={selected ? "primary" : "default"}
                          variant={selected ? "filled" : "outlined"}
                          onClick={() => handleToggle(role)}
                          sx={{
                            boxShadow: selected
                              ? "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px"
                              : "none",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              boxShadow:
                                "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
                              backgroundColor: selected
                                ? "primary.dark"
                                : "grey.100",
                            },
                            cursor: "pointer",
                          }}
                        />
                      );
                    })}
                  </div>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: "10px 25px" }}>
        <Button
          variant="outlined"
          onClick={() => {
            navigate("/users");
          }}
          color="primary"
          size="small"
          style={{ width: "120px" }}
        >
          <Typography>Откажи</Typography>
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            create();
          }}
          color="primary"
          size="small"
          style={{ width: "120px" }}
        >
          <Typography>Зачувај</Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
}
